import { CartType, OptionalAssets, SupportedCurrencies } from '@monorepo/types';
import CrudService from '../core/crud-service';

export interface QuickPurchaseInitResponse {
  asset: OptionalAssets;
  currency: SupportedCurrencies;
}

export interface QuickPurchasePrepareCartResponse {
  cart: CartType;
}

export interface SendOtpResponse {
  isExistingUser: boolean;
  sessionId: string;
}

export interface VerifyOtpResponse {
  isExistingUser: boolean;
}

export interface CreateAnonymousUserResponse {
  userId: string;
  sessionJwt: string;
}

export default class QuickPurchaseService extends CrudService {
  static override route = 'quick-purchase';

  async init(
    programId: string,
    currency?: string,
  ): Promise<QuickPurchaseInitResponse> {
    const response: { data: QuickPurchaseInitResponse } =
      await this.httpService.get(`${this.path}/init`, {
        params: { programId, currency },
      });
    return response.data;
  }

  /**
   * Prepares the cart for the authenticated user.
   * Must be called after authentication (session cookies already set).
   * The backend derives userId from the JWT session.
   */
  async prepareCart(
    programId: string,
    currency?: string,
  ): Promise<QuickPurchasePrepareCartResponse> {
    const response: { data: QuickPurchasePrepareCartResponse } =
      await this.httpService.post(`${this.path}/prepare-cart`, {
        programId,
        currency,
      });
    return response.data;
  }

  /**
   * Sends a 6-digit verification code to the given email.
   * Returns { isExistingUser, sessionId } — sessionId must be stored and passed
   * to verifyOtp and createAnonymousUser.
   */
  async sendOtp(email: string): Promise<SendOtpResponse> {
    const response: { data: SendOtpResponse } = await this.httpService.post(
      `${this.path}/otp/send`,
      { email },
    );
    return response.data;
  }

  /**
   * Verifies the 6-digit code using sessionId as the lookup key.
   * Returns { isExistingUser } — no session is set at this point.
   */
  async verifyOtp(sessionId: string, code: string): Promise<VerifyOtpResponse> {
    const response: { data: VerifyOtpResponse } = await this.httpService.post(
      `${this.path}/otp/verify`,
      { sessionId, code },
    );
    return response.data;
  }

  /**
   * Creates a Descope anonymous session for both new and existing users.
   * For new users: country is required and a lean DB record is created.
   * For existing users: country is omitted and no DB record is created.
   * Backend sets session cookies on success.
   */
  async createAnonymousUser(
    sessionId: string,
    email: string,
    country?: string,
  ): Promise<CreateAnonymousUserResponse> {
    const response: { data: CreateAnonymousUserResponse } =
      await this.httpService.post(`${this.path}/create-anonymous-user`, {
        sessionId,
        email,
        country,
      });
    return response.data;
  }

  /**
   * Fire-and-forget: logs a login redirect event server-side so Coralogix
   * captures the client IP. Never throws — errors are silently swallowed.
   */
  async logRedirect(reason: string): Promise<void> {
    try {
      await this.httpService.post(`${this.path}/log-redirect`, { reason });
    } catch {
      // Non-fatal — must never block the redirect
    }
  }

  async convertAnonymousUser(
    firstName: string,
    lastName: string,
  ): Promise<{ success: boolean }> {
    const response: { data: { success: boolean } } =
      await this.httpService.patch(`${this.path}/convert-anonymous-user`, {
        firstName,
        lastName,
      });
    return response.data;
  }
}
